import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { validateIpAddress } from '../../../utils/validation/banValidation';
import type { IpBan } from '../../../types/ban';

interface BanFormModalProps {
  ban: IpBan | null;
  onClose: () => void;
  onSubmit: (data: { ip_address?: string; reason?: string }) => void;
  isSubmitting: boolean;
}

export default function BanFormModal({
  ban,
  onClose,
  onSubmit,
  isSubmitting,
}: BanFormModalProps) {
  const { t } = useTranslation('bans');
  const [ipAddress, setIpAddress] = useState(ban?.ip_address || '');
  const [reason, setReason] = useState(ban?.reason || '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ban) {
      setIpAddress(ban.ip_address);
      setReason(ban.reason || '');
    }
  }, [ban]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate IP address only when creating new ban
    if (!ban) {
      const validationError = validateIpAddress(ipAddress);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Submit data
    const data: any = {};
    if (!ban) {
      data.ip_address = ipAddress;
    }
    if (reason.trim()) {
      data.reason = reason.trim();
    }

    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {ban ? t('modal.edit_title') : t('modal.add_title')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* IP Address field - only show when creating */}
            {!ban && (
              <div>
                <label htmlFor="ip_address" className="label">
                  {t('modal.ip_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="ip_address"
                  value={ipAddress}
                  onChange={(e) => {
                    setIpAddress(e.target.value);
                    setError(null);
                  }}
                  placeholder={t('modal.ip_placeholder')}
                  className="input w-full"
                  required
                  disabled={isSubmitting}
                />
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>
            )}

            {/* Show current IP when editing */}
            {ban && (
              <div>
                <label className="label">{t('modal.ip_label')}</label>
                <div className="px-3 py-2 bg-gray-100 rounded border border-gray-300">
                  <code className="text-sm">{ban.ip_address}</code>
                </div>
              </div>
            )}

            {/* Reason field */}
            <div>
              <label htmlFor="reason" className="label">
                {t('modal.reason_label')}
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('modal.reason_placeholder')}
                className="input w-full"
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('modal.char_count', { count: reason.length })}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                {t('actions.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {ban ? t('actions.updating') : t('actions.adding')}
                  </>
                ) : ban ? (
                  t('actions.update_ban')
                ) : (
                  t('actions.add_ban')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
